import apiConfig from '../config/api';

// ---- Wire types (mirror job_monitor/src/api/schemas/jobs.py) ----

export type JobStatus =
  | 'running'
  | 'starting'
  | 'stopping'
  | 'finished'
  | 'terminated'
  | 'failed'
  | 'never_run'
  | 'not_installed'
  | 'unknown';

export type ScheduleKind =
  | 'manual'
  | 'every_minutes'
  | 'every_hours'
  | 'every_days'
  | 'weekly'
  | 'windowed';

export type Weekday = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface Schedule {
  kind: ScheduleKind;
  interval: number | null;
  days_of_week: Weekday[];
  time_of_day: string | null;
  end_time_of_day: string | null;
  timezone: string;
}

export interface Job {
  job_id: string;
  display_name: string;
  description: string;
  service_unit: string;
  timer_unit: string | null;

  status: JobStatus;
  active_state: string;
  sub_state: string;
  pid: number | null;
  last_start_at: string | null;
  last_stop_at: string | null;
  last_exit_code: number | null;
  last_error: string | null;
  execution_id: string | null;
  service_enabled: boolean;

  schedulable: boolean;
  schedule: Schedule;
  schedule_enabled: boolean;
  schedule_summary: string;
  timer_active: boolean;
  next_run_at: string | null;
  last_trigger_at: string | null;

  job_type: string;
  params: Record<string, ParamValue>;
  cost_warning: string | null;
}

export type ParamType = 'string' | 'int' | 'bool' | 'date' | 'string_list' | 'symbol_list';

export type ParamValue = string | number | boolean | string[];

export interface ParamSpec {
  name: string;
  type: ParamType;
  required: boolean;
  default: ParamValue | null;
  description: string;
  choices: string[];
  minimum: number | null;
  maximum: number | null;
}

export interface JobType {
  job_type: string;
  display_name: string;
  description: string;
  image: string;
  timeout_seconds: number;
  max_parallel: number;
  memory: string | null;
  cpus: number | null;
  cost_warning: string | null;
  params: ParamSpec[];
}

export type LogLevel = 'trace' | 'debug' | 'info' | 'warning' | 'error' | 'critical';

export interface LogLine {
  timestamp: string | null;
  level: LogLevel;
  message: string;
  /** "job" for the job's own container, otherwise the service container's name. */
  source: string;
}

export interface LogEvent extends LogLine {
  execution_id: string | null;
}

/** The error lines of the latest run, as its log stream shows them. Older runs are not kept. */
export interface JobErrors {
  job_id: string;
  run_id: string | null;
  run_status: string | null;
  run_error: string | null;
  lines: LogLine[];
}

/** Sent once when a log stream opens: which sources it will carry. */
export interface LogStreamReady {
  job_id: string;
  run_id: string | null;
  live: boolean;
  sources: string[];
}

export const JOB_LOG_SOURCE = 'job';

export type JobAction = 'start' | 'stop' | 'restart';

export const WEEKDAYS: Array<{ value: Weekday; label: string }> = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

export const SCHEDULE_KIND_OPTIONS: Array<{ value: ScheduleKind; label: string }> = [
  { value: 'manual', label: 'Manual only — no automatic runs' },
  { value: 'every_minutes', label: 'Every X minutes' },
  { value: 'every_hours', label: 'Every X hours' },
  { value: 'every_days', label: 'Every X days' },
  { value: 'weekly', label: 'Selected days of the week, at a time' },
  { value: 'windowed', label: 'Selected days — start at a time, then repeat until an end time' },
];

/** The zone this browser is in. Every new time-of-day schedule is written in it. */
export const browserTimeZone = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Every zone this browser knows, with the local one and UTC offered first. */
export const timeZoneOptions = (): string[] => {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: 'timeZone') => string[] };
  return Array.from(new Set([browserTimeZone(), 'UTC', ...(intl.supportedValuesOf?.('timeZone') ?? [])]));
};

export const DEFAULT_SCHEDULE: Schedule = {
  kind: 'manual',
  interval: null,
  days_of_week: [],
  time_of_day: null,
  end_time_of_day: null,
  timezone: 'UTC',
};

export const sameSchedule = (a: Schedule, b: Schedule): boolean =>
  a.kind === b.kind &&
  a.interval === b.interval &&
  a.time_of_day === b.time_of_day &&
  a.end_time_of_day === b.end_time_of_day &&
  a.timezone === b.timezone &&
  a.days_of_week.length === b.days_of_week.length &&
  a.days_of_week.every((day, index) => day === b.days_of_week[index]);

// ---- API calls ----

const errorMessage = (status: number, body: string): string => {
  if (status === 403) return 'You are not authorised to use the job monitor.';
  if (!body) return `Request failed with status ${status}`;
  try {
    return (JSON.parse(body) as { detail?: string }).detail ?? body;
  } catch {
    return body;
  }
};

const request = async <T,>(path: string, token: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(`${apiConfig.jobMonitorApiUrl}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  const text = await response.text();
  if (!response.ok) {
    throw new Error(errorMessage(response.status, text));
  }

  return text ? (JSON.parse(text) as T) : ({} as T);
};

export const listJobs = async (token: string): Promise<Job[]> =>
  (await request<{ jobs: Job[] }>('/jobs', token)).jobs;

export const listJobTypes = async (token: string): Promise<JobType[]> =>
  (await request<{ job_types: JobType[] }>('/job-types', token)).job_types;

/** Change what a job runs with. The type's own schema is enforced server side. */
export const updateJobParams = (
  jobId: string,
  params: Record<string, ParamValue>,
  token: string,
): Promise<Job> =>
  request<Job>(`/jobs/${encodeURIComponent(jobId)}`, token, {
    method: 'PATCH',
    body: JSON.stringify({ params }),
  });

export const runJobAction = (jobId: string, action: JobAction, token: string): Promise<Job> =>
  request<Job>(`/jobs/${encodeURIComponent(jobId)}/${action}`, token, { method: 'POST' });

export const updateJobSchedule = (
  jobId: string,
  schedule: Schedule,
  enabled: boolean,
  token: string,
): Promise<Job> =>
  request<Job>(`/jobs/${encodeURIComponent(jobId)}/schedule`, token, {
    method: 'PUT',
    body: JSON.stringify({ schedule, enabled }),
  });

/** Removes the timer. The job stays registered and can still be run by hand. */
export const clearJobSchedule = (jobId: string, token: string): Promise<Job> =>
  request<Job>(`/jobs/${encodeURIComponent(jobId)}/schedule`, token, { method: 'DELETE' });

/** Removes the job completely: units, registry entry, and all stored data. */
export const deleteJob = (jobId: string, token: string): Promise<void> =>
  request<void>(`/jobs/${encodeURIComponent(jobId)}`, token, { method: 'DELETE' });

export const fetchJobErrors = (jobId: string, token: string): Promise<JobErrors> =>
  request<JobErrors>(`/jobs/${encodeURIComponent(jobId)}/errors`, token);

/** The latest run's whole output as plain text, for saving to a file. */
export const fetchJobLogText = async (jobId: string, token: string): Promise<string> => {
  const response = await fetch(
    `${apiConfig.jobMonitorApiUrl}/jobs/${encodeURIComponent(jobId)}/logs/download`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  const text = await response.text();
  if (!response.ok) {
    throw new Error(errorMessage(response.status, text));
  }
  return text;
};

/**
 * Open the SSE log stream. EventSource cannot send headers, so the verified
 * token travels as a query parameter — the same check runs server side.
 */
export const openLogStream = (jobId: string, token: string): EventSource =>
  new EventSource(
    `${apiConfig.jobMonitorApiUrl}/jobs/${encodeURIComponent(jobId)}/logs/stream?token=${encodeURIComponent(token)}`,
  );
